/**
 * ProjectTeamModal - modal for managing project team members
 */

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { clsx } from 'clsx';
import { X, UserPlus, Crown, Shield, User, Eye, Trash2, Check } from 'lucide-react';
import { useProjectStore } from '@stores/projectStore';
import { organizationsApi, type OrganizationMember } from '@services/api';
import { toast } from '@components/ui/Toast';
import type { ProjectMember } from '@services/api';
import Avatar from '@components/ui/Avatar';
import { useConfirmDialog } from '@/components/ui/ConfirmDialog';

interface ProjectTeamModalProps {
  projectId: string;
  organizationId: string;
  open: boolean;
  onClose: () => void;
}

type MemberRole = 'owner' | 'admin' | 'member' | 'viewer';

const roleConfig: Record<MemberRole, { labelKey: string; icon: typeof Crown; color: string }> = {
  owner: { labelKey: 'roles.owner', icon: Crown, color: 'text-yellow-500' },
  admin: { labelKey: 'roles.admin', icon: Shield, color: 'text-blue-500' },
  member: { labelKey: 'roles.member', icon: User, color: 'text-neutral-500' },
  viewer: { labelKey: 'roles.viewer', icon: Eye, color: 'text-neutral-400' },
};

export default function ProjectTeamModal({
  projectId,
  organizationId,
  open,
  onClose,
}: ProjectTeamModalProps) {
  const { t } = useTranslation();
  const {
    projectMembers,
    fetchProjectMembers,
    addProjectMember,
    updateMemberRole,
    removeProjectMember,
  } = useProjectStore();

  const [isLoading, setIsLoading] = useState(false);
  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [orgMembers, setOrgMembers] = useState<OrganizationMember[]>([]);
  const [loadingOrgMembers, setLoadingOrgMembers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const [selectedRole, setSelectedRole] = useState<'admin' | 'member' | 'viewer'>('member');
  const [addingMember, setAddingMember] = useState(false);

  // Fetch project members when modal opens
  useEffect(() => {
    if (open && projectId) {
      setIsLoading(true);
      fetchProjectMembers(projectId)
        .catch((err) => {
          console.error('Failed to fetch project members:', err);
          toast.error(t('project.loadTeamError'));
        })
        .finally(() => setIsLoading(false));
    }
  }, [open, projectId, fetchProjectMembers, t]);

  // Fetch organization members when showing add member UI
  useEffect(() => {
    if (showAddMember && organizationId) {
      setLoadingOrgMembers(true);
      organizationsApi
        .listMembers(organizationId)
        .then((members) => setOrgMembers(members))
        .catch((err) => {
          console.error('Failed to fetch org members:', err);
          toast.error(t('project.loadOrgMembersError'));
        })
        .finally(() => setLoadingOrgMembers(false));
    }
  }, [showAddMember, organizationId, t]);

  // Filter org members to only show those not already in project
  const availableMembers = useMemo(() => {
    const projectMemberIds = new Set(projectMembers.map((m) => m.user.id));
    return orgMembers.filter((m) => !projectMemberIds.has(m.user.id));
  }, [orgMembers, projectMembers]);

  const handleRoleChange = async (member: ProjectMember, newRole: string) => {
    if (member.role === 'owner') {
      toast.error(t('project.cannotChangeOwnerRole'));
      return;
    }

    setUpdatingMemberId(member.user.id);
    try {
      await updateMemberRole(projectId, member.user.id, newRole);
      toast.success(t('project.updateRoleSuccess'));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('project.updateRoleError');
      toast.error(message);
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleRemoveMember = async (member: ProjectMember) => {
    if (member.role === 'owner') {
      toast.error(t('project.cannotRemoveOwner'));
      return;
    }

    const confirmed = await confirm({
      title: t('project.confirmRemoveMemberTitle'),
      description: t('project.confirmRemoveMemberDescription', { name: member.user.displayName }),
      confirmLabel: t('project.confirmRemoveMemberLabel'),
      variant: 'danger',
    });

    if (!confirmed) return;

    setUpdatingMemberId(member.user.id);
    try {
      await removeProjectMember(projectId, member.user.id);
      toast.success(t('project.removeMemberSuccess'));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('project.removeMemberError');
      toast.error(message);
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUserId) {
      toast.error(t('project.selectMemberError'));
      return;
    }

    setAddingMember(true);
    try {
      await addProjectMember(projectId, selectedUserId, selectedRole);
      toast.success(t('project.addMemberSuccess'));
      setShowAddMember(false);
      setSelectedUserId(null);
      setSelectedRole('member');
      // Refresh project members
      await fetchProjectMembers(projectId);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('project.addMemberError');
      toast.error(message);
    } finally {
      setAddingMember(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={clsx(
            'fixed inset-0 z-50 bg-black/50',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          )}
        />
        <Dialog.Content
          className={clsx(
            'fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2',
            'max-h-[90vh] overflow-y-auto',
            'rounded-lg bg-white p-6 shadow-xl dark:bg-neutral-800',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95'
          )}
        >
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-white">
              {t('project.projectTeam')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className={clsx(
                  'rounded p-1 transition-colors',
                  'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700',
                  'dark:hover:bg-neutral-700 dark:hover:text-neutral-300'
                )}
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Add member section */}
          {showAddMember ? (
            <div className="mb-4 rounded-lg border border-neutral-200 p-4 dark:border-neutral-700">
              <h4 className="mb-3 text-sm font-medium text-neutral-900 dark:text-white">
                {t('project.addTeamMember')}
              </h4>
              {loadingOrgMembers ? (
                <div className="flex justify-center py-4">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
                </div>
              ) : availableMembers.length === 0 ? (
                <p className="py-2 text-sm text-neutral-500 dark:text-neutral-400">
                  {t('project.allMembersAdded')}
                </p>
              ) : (
                <>
                  {/* Member selection */}
                  <div className="mb-3 space-y-2">
                    <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400">
                      {t('project.selectMember')}
                    </label>
                    <div className="max-h-40 overflow-y-auto rounded-md border border-neutral-300 dark:border-neutral-600">
                      {availableMembers.map((member) => (
                        <button
                          key={member.user.id}
                          type="button"
                          onClick={() => setSelectedUserId(member.user.id)}
                          className={clsx(
                            'flex w-full items-center gap-3 px-3 py-2 text-left',
                            'hover:bg-neutral-50 dark:hover:bg-neutral-700',
                            selectedUserId === member.user.id &&
                              'bg-primary-50 dark:bg-primary-900/30'
                          )}
                        >
                          <Avatar
                            name={member.user.displayName || member.user.email}
                            size="sm"
                            {...(member.user.avatarUrl && { src: member.user.avatarUrl })}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="truncate text-sm font-medium text-neutral-900 dark:text-white">
                              {member.user.displayName || member.user.email}
                            </p>
                            <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
                              {member.user.email}
                            </p>
                          </div>
                          {selectedUserId === member.user.id && (
                            <Check className="h-4 w-4 text-primary-600" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Role selection */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                      {t('project.role')}
                    </label>
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value as typeof selectedRole)}
                      className={clsx(
                        'w-full rounded-md border px-3 py-2 text-sm',
                        'border-neutral-300 bg-white text-neutral-900',
                        'dark:border-neutral-600 dark:bg-neutral-700 dark:text-white',
                        'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                      )}
                    >
                      <option value="admin">{t('project.roleAdmin')}</option>
                      <option value="member">{t('project.roleMember')}</option>
                      <option value="viewer">{t('project.roleViewer')}</option>
                    </select>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddMember(false);
                        setSelectedUserId(null);
                      }}
                      className={clsx(
                        'rounded-md px-3 py-1.5 text-sm font-medium',
                        'text-neutral-600 hover:bg-neutral-100',
                        'dark:text-neutral-400 dark:hover:bg-neutral-700'
                      )}
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={handleAddMember}
                      disabled={!selectedUserId || addingMember}
                      className={clsx(
                        'rounded-md px-3 py-1.5 text-sm font-medium',
                        'bg-primary-600 text-white hover:bg-primary-700',
                        'disabled:cursor-not-allowed disabled:opacity-50'
                      )}
                    >
                      {addingMember ? t('project.addingMember') : t('project.addMember')}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="mb-4">
              <button
                onClick={() => setShowAddMember(true)}
                className={clsx(
                  'flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed',
                  'border-neutral-300 px-4 py-3 text-sm font-medium',
                  'text-neutral-600 hover:border-primary-500 hover:text-primary-600',
                  'dark:border-neutral-600 dark:text-neutral-400',
                  'dark:hover:border-primary-400 dark:hover:text-primary-400'
                )}
              >
                <UserPlus className="h-4 w-4" />
                {t('project.addMember')}
              </button>
            </div>
          )}

          {/* Members list */}
          <div className="space-y-2">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
              </div>
            ) : projectMembers.length === 0 ? (
              <p className="py-8 text-center text-neutral-500 dark:text-neutral-400">
                {t('project.noTeamMembers')}
              </p>
            ) : (
              projectMembers.map((member) => {
                const config = roleConfig[member.role];
                const RoleIcon = config.icon;
                const isUpdating = updatingMemberId === member.user.id;

                return (
                  <div
                    key={member.id}
                    className={clsx(
                      'flex items-center justify-between rounded-lg p-3',
                      'bg-neutral-50 dark:bg-neutral-900',
                      isUpdating && 'opacity-50'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar
                        name={member.user.displayName}
                        size="md"
                        {...(member.user.avatarUrl && { src: member.user.avatarUrl })}
                      />
                      <div>
                        <p className="font-medium text-neutral-900 dark:text-white">
                          {member.user.displayName}
                        </p>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">
                          {member.user.email}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {member.role === 'owner' ? (
                        <div
                          className={clsx(
                            'flex items-center gap-1.5 rounded-md px-2 py-1',
                            'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                          )}
                        >
                          <RoleIcon className="h-3.5 w-3.5" />
                          <span className="text-sm font-medium">{t('project.ownerBadge')}</span>
                        </div>
                      ) : (
                        <>
                          <select
                            value={member.role}
                            onChange={(e) => handleRoleChange(member, e.target.value)}
                            disabled={isUpdating}
                            className={clsx(
                              'rounded-md border px-2 py-1 text-sm',
                              'border-neutral-300 bg-white text-neutral-700',
                              'dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200',
                              'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                              'disabled:cursor-not-allowed disabled:opacity-50'
                            )}
                          >
                            <option value="admin">{t('roles.admin')}</option>
                            <option value="member">{t('roles.member')}</option>
                            <option value="viewer">{t('roles.viewer')}</option>
                          </select>
                          <button
                            onClick={() => handleRemoveMember(member)}
                            disabled={isUpdating}
                            className={clsx(
                              'rounded p-1.5 transition-colors',
                              'text-neutral-400 hover:bg-red-100 hover:text-red-600',
                              'dark:hover:bg-red-900/30 dark:hover:text-red-400',
                              'disabled:cursor-not-allowed disabled:opacity-50'
                            )}
                            title={t('project.removeMemberTooltip')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className={clsx(
                'rounded-md px-4 py-2 text-sm font-medium',
                'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
                'dark:bg-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-600'
              )}
            >
              {t('common.close')}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
      {ConfirmDialogComponent}
    </Dialog.Root>
  );
}
